import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { config } from "@/config";
import { SettingsApi } from "@/api/giftcard.api";
import { GiftCardApi } from "@/api/giftcard.api";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Plus,
  Eye,
  CreditCard,
  X,
  EyeOff,
  Download,
  QrCode,
  RotateCcw,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { PaginationControls } from "@/components/common/PaginationControl";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

interface GiftCard {
  id: number;
  giftCardId: number;
  name: string;
  mobileNo: string | null;
  email: string;
  amount: number;
  balance: number;
  expiry: string;
  isSent: boolean;
  sendOn: string;
  isActive: boolean;
  createdBy: number;
  createdDateTime: string;
  issuedCardNumber: string | null;
  qrCode: string | null;
  issuedCardStatus?: string;
  createdByName?: string | null;
  personalMessage?: string | null;
  occasion?: string | null;
  giftCardName?: string | null;
  redemptionType?: string | null;
}

interface GiftCardHistoryEntry {
  merchantName: string;
  amountDeducted: number;
  redeemedDate: string;
}

const BULK_UPLOAD_SAMPLE_HEADERS =
  "GiftCardId,RecipientName,RecipientEmail,MobileNumber";

function downloadIssuedCardsBulkUploadSample() {
  const csv = `\ufeff${BULK_UPLOAD_SAMPLE_HEADERS}\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "issued-cards-bulk-upload-sample.csv";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const AllGiftCards = () => {
  const navigate = useNavigate();
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [totalGiftCards, setTotalGiftCards] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState<File | null>(null);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const bulkUploadInputRef = useRef<HTMLInputElement>(null);
  const [selectedGiftCard, setSelectedGiftCard] = useState<GiftCard | null>(
    null
  );
  const [isUnmasked, setIsUnmasked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [giftCardHistory, setGiftCardHistory] = useState<
    GiftCardHistoryEntry[]
  >([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [malls, setMalls] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedMallId, setSelectedMallId] = useState<number | null>(null);
  const [isMallLoading, setIsMallLoading] = useState(false);
  const hasInitialFetchRef = useRef(false);

  // Pagination - use backend totalCount for accurate page count
  const totalPages = Math.max(
    1,
    Math.ceil((totalGiftCards || 0) / (rowsPerPage || 1))
  );

  // Define fetchIssuedGiftCards first so it can be called from fetchMalls
  const fetchIssuedGiftCards = async (options?: {
    page?: number;
    pageSize?: number;
    mallId?: number | null;
  }) => {
    setIsLoading(true);
    try {
      // Get userId from sessionStorage
      const userInfo = sessionStorage.getItem("userInfo");
      const userId = userInfo ? JSON.parse(userInfo).userId : 0;

      // Resolve pagination and mall values
      const currentPage = options?.page ?? page;
      const currentPageSize = options?.pageSize ?? rowsPerPage;
      const mallIdToUse =
        options?.mallId !== undefined ? options.mallId : selectedMallId ?? 0;

      const response = await axios.post(
        `${config.GIFT_API_BASE_URL}/GiftCard/GetIssuedGiftCardDetails`,
        {
          p_Id: userId,
          p_MallId: mallIdToUse,
          // Backend expects 1-based page index
          p_Page: currentPage + 1,
          p_PageSize: currentPageSize,
        },
        {
          headers: {
            XApiKey: config.GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.listCardIssuance) {
        const list = response.data.listCardIssuance;
        setGiftCards(list);
        setTotalGiftCards(response.data.totalCount ?? list.length ?? 0);
      }
    } catch (error) {
      console.error("Error fetching issued gift cards:", error);
      toast.error("Failed to load gift cards. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch malls
  const fetchMalls = async () => {
    setIsMallLoading(true);
    try {
      const data = await SettingsApi.getMallDetails();
      const mallsData = Array.isArray(data?.malldetails)
        ? data.malldetails
        : [];
      const mapped = mallsData.map((mall: any) => ({
        id: mall.mallId,
        name: mall.mallName || `Mall ${mall.mallId}`,
      }));
      setMalls(mapped);

      // Pre-select the first mall immediately after malls are loaded
      if (mapped.length > 0) {
        const firstMallId = mapped[0].id;
        setSelectedMallId(firstMallId);
        // Call fetchIssuedGiftCards directly with the first mall ID and initial pagination
        await fetchIssuedGiftCards({
          page: 0,
          pageSize: rowsPerPage,
          mallId: firstMallId,
        });
        hasInitialFetchRef.current = true;
      }
    } catch (error) {
      console.error("Error fetching malls:", error);
      setMalls([]);
      // If malls fail to load, only then use mallId 0
      setSelectedMallId(null);
      await fetchIssuedGiftCards({
        page: 0,
        pageSize: rowsPerPage,
        mallId: 0,
      });
      hasInitialFetchRef.current = true;
    } finally {
      setIsMallLoading(false);
    }
  };

  // Fetch issued gift cards
  useEffect(() => {
    fetchMalls();
  }, []);

  // Fetch gift cards when selectedMallId changes (user manually changes mall)
  useEffect(() => {
    // Only fetch if initial fetch is done and user is changing the mall
    if (
      hasInitialFetchRef.current &&
      selectedMallId !== null &&
      !isMallLoading
    ) {
      // Reset to first page when mall changes
      setPage(0);
      fetchIssuedGiftCards({
        page: 0,
        pageSize: rowsPerPage,
        mallId: selectedMallId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMallId]);

  // Reset filters
  const handleReset = () => {
    setSearchTerm("");
    setPage(0);
    // Reset to first mall option (same as initial load)
    if (malls.length > 0) {
      const firstMallId = malls[0].id;
      setSelectedMallId(firstMallId);
      // Fetch gift cards with the first mall and first page
      fetchIssuedGiftCards({
        page: 0,
        pageSize: rowsPerPage,
        mallId: firstMallId,
      });
    } else {
      setSelectedMallId(null);
    }
  };

  // Centralized handler for page changes (0-based index)
  const handlePageChange = (newPage: number) => {
    if (newPage < 0 || newPage > totalPages - 1) return;
    setPage(newPage);
    fetchIssuedGiftCards({
      page: newPage,
      pageSize: rowsPerPage,
      mallId: selectedMallId,
    });
  };

  const handleViewDetails = (giftCard: GiftCard) => {
    setSelectedGiftCard(giftCard);
    setIsModalOpen(true);
    setIsUnmasked(false); // Reset unmask state when opening modal
  };

  const getFullCardNumber = (cardNumber: string) => {
    if (!cardNumber) return "************";

    // Remove all spaces and get only digits
    const digits = cardNumber.replace(/\s/g, "").replace(/\D/g, "");

    if (digits.length < 4) return "************";

    // Add spaces every 4 characters
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };

  useEffect(() => {
    if (!isModalOpen) {
      setGiftCardHistory([]);
      setHistoryError(null);
      setHistoryVisible(false);
      setIsHistoryLoading(false);
    }
  }, [isModalOpen]);

  // Function to format card number with masking and spacing
  const formatCardNumber = (cardNumber: string | null) => {
    // If cardNumber is null or empty, return empty string
    if (!cardNumber || cardNumber.trim() === "") return "";

    // Remove all spaces and get only digits
    const digits = cardNumber.replace(/\s/g, "").replace(/\D/g, "");

    if (digits.length < 4) return "";

    // Get last 4 digits
    const lastFour = digits.slice(-4);

    // For 12-digit cards, show first 8 as masked, last 4 as visible
    // Format: **** **** XXXX
    return `**** **** ${lastFour}`;
  };

  const toggleUnmask = () => {
    setIsUnmasked(!isUnmasked);
  };

  const fetchRedemptionHistory = async (issuedCardNumber: string) => {
    const sanitizedCardNumber = issuedCardNumber.replace(/\s+/g, "");
    if (!sanitizedCardNumber) {
      toast.error("Card number not available for history.");
      return;
    }

    setIsHistoryLoading(true);
    setHistoryError(null);
    try {
      const userInfo = sessionStorage.getItem("userInfo");
      const userId = userInfo ? JSON.parse(userInfo).userId ?? 0 : 0;

      const response = await fetch(
        `${config.GIFT_API_BASE_URL}/GiftCard/GetGiftCardHistory`,
        {
          method: "POST",
          headers: {
            accept: "text/plain",
            "Content-Type": "application/json",
            XApiKey: config.GIFT_API_KEY,
          },
          body: JSON.stringify({
            userId,
            issuedGiftCardNo: sanitizedCardNumber,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch redemption history");
      }

      const data = await response.json();
      let entries: GiftCardHistoryEntry[] = [];
      if (Array.isArray(data)) {
        entries = data;
      } else if (Array.isArray(data?.cardHistory)) {
        entries = data.cardHistory;
      } else if (Array.isArray(data?.history)) {
        entries = data.history;
      } else if (
        data &&
        (data.merchantName || data.amountDeducted || data.redeemedDate)
      ) {
        entries = [data];
      }

      entries = entries
        .map((entry: any) => ({
          merchantName: entry.merchantName || "N/A",
          amountDeducted: parseFloat(entry.amountDeducted) || 0,
          redeemedDate: entry.redeemedDate || "",
        }))
        .filter(
          (entry) =>
            entry.merchantName || entry.amountDeducted || entry.redeemedDate
        );

      setGiftCardHistory(entries);
      if (entries.length === 0) {
        setHistoryError("No redemption history found.");
      }
      setHistoryVisible(true);
    } catch (error) {
      console.error("History fetch error:", error);
      setHistoryError("Failed to load redemption history. Please try again.");
      setGiftCardHistory([]);
      setHistoryVisible(true);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  // Function to download QR code via API
  const downloadQRCode = async () => {
    if (!selectedGiftCard) {
      toast.error("No gift card selected");
      return;
    }

    const rawCardNumber = selectedGiftCard.issuedCardNumber || "";
    const sanitizedCardNumber = rawCardNumber.replace(/\s+/g, "");
    if (!sanitizedCardNumber) {
      toast.error("Card number not available for QR download.");
      return;
    }

    // Get username from sessionStorage (same pattern as other gift APIs)
    const userInfoStr = sessionStorage.getItem("userInfo");
    const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
    const userName = userInfo?.username || "";

    try {
      const response = await axios.post(
        `${config.GIFT_API_BASE_URL}/GiftCard/DownloadQRCode`,
        JSON.stringify({
          userName,
          cardNumber: sanitizedCardNumber,
        }),
        {
          headers: {
            XApiKey: config.GIFT_API_KEY,
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      // Create a download link for the returned image/blob
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `giftcard-qr-${sanitizedCardNumber}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      toast.success("QR code downloaded successfully.");
    } catch (error) {
      console.error("Error downloading QR code:", error);
      toast.error("Failed to download QR code. Please try again.");
    }
  };

  // Function to print QR code
  const printQRCode = () => {
    if (!selectedGiftCard.qrCode) return;

    try {
      // Create a new window for printing
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error("Please allow popups to print QR code");
        return;
      }

      // Get currency from localStorage
      const currency = localStorage.getItem("giftCardCurrency") || "USD";

      // Create print content
      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Gift Card QR Code</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                text-align: center;
                padding: 20px;
                margin: 0;
              }
              .qr-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
              }
              .qr-image {
                max-width: 300px;
                max-height: 300px;
                border: 2px solid #000;
                margin: 20px 0;
              }
              .qr-title {
                font-size: 24px;
                font-weight: bold;
                margin-bottom: 10px;
              }
              .qr-info {
                font-size: 16px;
                color: #666;
                margin-top: 20px;
              }
              @media print {
                body { margin: 0; }
                .qr-container { min-height: auto; }
              }
            </style>
          </head>
          <body>
            <div class="qr-container">
              <div class="qr-title">Gift Card QR Code</div>
              <img src="${
                selectedGiftCard.qrCode
              }" alt="Gift Card QR Code" class="qr-image" />
              <div class="qr-info">
                Card Number: ${getFullCardNumber(
                  selectedGiftCard.issuedCardNumber
                )}<br>
                Amount: ${selectedGiftCard.amount.toFixed(2)} ${currency}<br>
                Issued To: ${selectedGiftCard.name}
              </div>
            </div>
          </body>
        </html>
      `;

      printWindow.document.write(printContent);
      printWindow.document.close();

      // Wait for image to load, then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      };

      toast.success("Print dialog opened!");
    } catch (error) {
      console.error("Error printing QR code:", error);
      toast.error("Failed to print QR code");
    }
  };

  const getStatusBadge = (status: string | undefined) => {
    if (!status) {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          N/A
        </span>
      );
    }

    const normalized = status.trim().toLowerCase();
    let color = "bg-gray-100 text-gray-800";

    // Map the three known statuses to distinct colors
    if (normalized === "not redeemed") {
      color = "bg-gray-100 text-gray-800";
    } else if (normalized === "partially redeemed") {
      color = "bg-yellow-100 text-yellow-800";
    } else if (normalized === "fully redeemed") {
      color = "bg-green-100 text-green-800";
    }

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        {status}
      </span>
    );
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadFile) return;

    setIsBulkUploading(true);
    try {
      const response = await GiftCardApi.bulkUploadGiftCardIssuance(
        bulkUploadFile
      );

      if (response?.error_msg) {
        toast.error(response.error_msg);
        return;
      }

      toast.success(response?.message || "Bulk upload completed successfully.");
      setBulkUploadOpen(false);
      setBulkUploadFile(null);
      if (bulkUploadInputRef.current) {
        bulkUploadInputRef.current.value = "";
      }
      await fetchIssuedGiftCards({
        page,
        pageSize: rowsPerPage,
        mallId: selectedMallId,
      });
    } catch (error: any) {
      console.error("Bulk upload error:", error);
      const message =
        error?.response?.data?.error_msg ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload file. Please try again.";
      toast.error(message);
    } finally {
      setIsBulkUploading(false);
    }
  };

  const columns = [
    {
      header: "Card Number",
      accessorKey: "issuedCardNumber" as keyof GiftCard,
      cell: (row: GiftCard) => {
        const formattedNumber = formatCardNumber(row.issuedCardNumber);
        return (
          <div className="flex items-center space-x-2">
            <CreditCard className="h-4 w-4 text-gray-500" />
            <span className="font-mono text-sm">
              {formattedNumber || "N/A"}
            </span>
          </div>
        );
      },
    },
    {
      header: "Amount",
      accessorKey: "amount" as keyof GiftCard,
      cell: (row: GiftCard) => {
        const currency = localStorage.getItem("giftCardCurrency") || "USD";
        return (
          <div className="font-medium">
            {row.amount.toFixed(2)} {currency}
          </div>
        );
      },
    },
    {
      header: "Balance",
      accessorKey: "balance" as keyof GiftCard,
      cell: (row: GiftCard) => {
        const currency = localStorage.getItem("giftCardCurrency") || "USD";
        const isZero = row.balance === 0;
        return (
          <div
            className={`font-medium ${
              isZero ? "text-red-600" : "text-green-600"
            }`}
          >
            {row.balance.toFixed(2)} {currency}
          </div>
        );
      },
    },
    {
      header: "Status",
      accessorKey: "issuedCardStatus" as keyof GiftCard,
      cell: (row: GiftCard) => getStatusBadge(row.issuedCardStatus),
    },
    {
      header: "Issued To",
      accessorKey: "name" as keyof GiftCard,
      cell: (row: GiftCard) => (
        <div>
          <div className="font-medium text-gray-900">{row.name}</div>
          <div className="text-sm text-gray-500">{row.email}</div>
        </div>
      ),
    },
    {
      header: "Mobile Number",
      accessorKey: "mobileNo" as keyof GiftCard,
      cell: (row: GiftCard) => (
        <span className="text-gray-900">{row.mobileNo || "N/A"}</span>
      ),
    },
    {
      header: "Issued On",
      accessorKey: "createdDateTime" as keyof GiftCard,
      cell: (row: GiftCard) => (
        <div>
          <div className="text-sm">
            {format(new Date(row.createdDateTime), "MMM dd, yyyy")}
          </div>
          <div className="text-sm text-gray-500">
            {format(new Date(row.createdDateTime), "HH:mm")}
          </div>
        </div>
      ),
    },
    {
      header: "Actions",
      id: "actions",
      cell: (row: GiftCard) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleViewDetails(row)}
          className="h-8 px-3"
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Issued Cards</h1>
          <p className="text-gray-600 mt-1">
            Manage and view all issued gift cards in your system
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Mall Dropdown */}
          <div className="w-48">
            <Select
              value={
                selectedMallId !== null ? selectedMallId.toString() : undefined
              }
              onValueChange={(value) => {
                if (value === "clear") {
                  setSelectedMallId(null);
                  // If initial fetch is done and user is manually changing the mall, fetch immediately
                  if (hasInitialFetchRef.current) {
                    setPage(0);
                    fetchIssuedGiftCards({
                      page: 0,
                      pageSize: rowsPerPage,
                      mallId: null,
                    });
                  }
                } else {
                  const newMallId = Number(value);
                  setSelectedMallId(newMallId);
                  // If initial fetch is done and user is manually changing the mall, fetch immediately
                  if (hasInitialFetchRef.current) {
                    setPage(0);
                    fetchIssuedGiftCards({
                      page: 0,
                      pageSize: rowsPerPage,
                      mallId: newMallId,
                    });
                  }
                }
              }}
              disabled={isMallLoading || malls.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    isMallLoading ? "Loading malls..." : "Select mall"
                  }
                />
              </SelectTrigger>
              <SelectContent className="!max-h-48 overflow-auto py-0">
                <SelectItem value="clear" className="text-gray-500 italic">
                  Clear selection
                </SelectItem>
                {malls.length > 0 ? (
                  malls.map((mall) => (
                    <SelectItem key={mall.id} value={mall.id.toString()}>
                      {mall.name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    {isMallLoading ? "Loading..." : "No malls found"}
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              className="border-amber-700 text-amber-900 hover:bg-amber-50"
              onClick={() => {
                setBulkUploadFile(null);
                setBulkUploadOpen(true);
              }}
            >
              <Upload className="h-4 w-4 mr-2" />
              Bulk Upload
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => navigate("/giftcard/issue")}
            >
              <Plus className="h-4 w-4 mr-2" />
              Issue Cards
            </Button>
          </div>
        </div>
      </div>

      <Dialog
        open={bulkUploadOpen}
        onOpenChange={(open) => {
          setBulkUploadOpen(open);
          if (!open) {
            setBulkUploadFile(null);
            if (bulkUploadInputRef.current) {
              bulkUploadInputRef.current.value = "";
            }
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk upload</DialogTitle>
            <DialogDescription>
              Download the sample file, fill in your rows, then upload the
              completed file.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 tracking-tight">
                Sample file
              </h3>
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:w-auto gap-2"
                onClick={() => downloadIssuedCardsBulkUploadSample()}
              >
                <Download className="h-4 w-4" />
                Download sample file
              </Button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bulk-upload-file" className="text-sm font-medium">
                Upload file
              </Label>
              <input
                ref={bulkUploadInputRef}
                id="bulk-upload-file"
                type="file"
                accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  setBulkUploadFile(file);
                }}
              />
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => bulkUploadInputRef.current?.click()}
                >
                  Choose file
                </Button>
                <span className="text-sm text-gray-600 truncate">
                  {bulkUploadFile ? bulkUploadFile.name : "No file selected"}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setBulkUploadOpen(false);
                setBulkUploadFile(null);
                if (bulkUploadInputRef.current) {
                  bulkUploadInputRef.current.value = "";
                }
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={!bulkUploadFile || isBulkUploading}
              onClick={handleBulkUpload}
            >
              {isBulkUploading ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {/* Reset Button */}
            <Button variant="outline" onClick={handleReset} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-6">
              {[1, 2, 3, 4, 5].map((row) => (
                <div
                  key={`skeleton-row-${row}`}
                  className="h-12 w-full rounded-md bg-gray-100 animate-pulse"
                />
              ))}
            </div>
          ) : (
            <DataTable
              title="All Issued Cards"
              data={giftCards}
              columns={columns}
            />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!isLoading && (
        <div className="mt-4 flex justify-between items-center">
          {/* Rows per page selector */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Rows per page:</span>
            <Select
              value={rowsPerPage.toString()}
              onValueChange={(value) => {
                const newRowsPerPage = parseInt(value, 10);
                setRowsPerPage(newRowsPerPage);
                setPage(0); // Reset to first page when changing rows per page
                fetchIssuedGiftCards({
                  page: 0,
                  pageSize: newRowsPerPage,
                  mallId: selectedMallId,
                });
              }}
            >
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="30">30</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              onPrevHandler={() => handlePageChange(page - 1)}
              onNextHandler={() => handlePageChange(page + 1)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}

      {/* Gift Card Details Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4"></DialogHeader>

          {selectedGiftCard && (
            <div className="space-y-8">
              {/* Card Details Section */}
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900 border-b pb-3">
                  Card Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Card Number */}
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-500">
                        Card Number
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={toggleUnmask}
                        className="h-8 w-8 p-0"
                        title={
                          isUnmasked ? "Hide card number" : "Show card number"
                        }
                      >
                        {isUnmasked ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CreditCard className="h-4 w-4 text-gray-500" />
                      <span className="font-mono text-lg">
                        {isUnmasked
                          ? getFullCardNumber(selectedGiftCard.issuedCardNumber)
                          : formatCardNumber(selectedGiftCard.issuedCardNumber)}
                      </span>
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500">
                      Amount
                    </h3>
                    <div className="text-lg font-semibold">
                      {selectedGiftCard.amount.toFixed(2)}{" "}
                      {localStorage.getItem("giftCardCurrency") || "USD"}
                    </div>
                  </div>

                  {/* Remaining Balance */}
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500">
                      Remaining Balance
                    </h3>
                    <div
                      className={`text-lg font-semibold ${
                        selectedGiftCard.balance === 0
                          ? "text-red-600"
                          : "text-green-600"
                      }`}
                    >
                      {selectedGiftCard.balance.toFixed(2)}{" "}
                      {localStorage.getItem("giftCardCurrency") || "USD"}
                    </div>
                  </div>

                  {/* Gift Card Name */}
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500">
                      Gift Card Name
                    </h3>
                    <div className="text-lg font-semibold">
                      {selectedGiftCard.giftCardName ?? "-"}
                    </div>
                  </div>

                  {/* Redemption Type */}
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500">
                      Redemption Type
                    </h3>
                    <div className="text-lg font-semibold">
                      {selectedGiftCard.redemptionType ?? "-"}
                    </div>
                  </div>
                </div>
              </div>

              {/* QR Code Section - Only show when unmasked */}
              {isUnmasked && (
                <div className="space-y-6">
                  <h2 className="text-lg font-semibold text-gray-900 border-b pb-3">
                    QR Code
                  </h2>
                  <div className="p-6 bg-gray-50 rounded-lg">
                    <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6">
                      {/* QR Code Image */}
                      <div className="flex-shrink-0">
                        <div className="w-32 h-32 bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center">
                          {selectedGiftCard.qrCode ? (
                            <img
                              src={selectedGiftCard.qrCode}
                              alt="Gift Card QR Code"
                              className="w-full h-full object-contain rounded"
                            />
                          ) : (
                            <div className="text-center">
                              <QrCode className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                              <p className="text-xs text-gray-500">
                                No QR Code Found
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* QR Code Info */}
                      <div className="flex-1 space-y-3">
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-2">
                            Gift Card QR Code
                          </h3>
                          <p className="text-sm text-gray-600">
                            {selectedGiftCard.qrCode
                              ? "This QR code contains your gift card information and can be scanned at participating merchants to redeem your gift card."
                              : "QR code is not available for this gift card."}
                          </p>
                        </div>

                        {selectedGiftCard.qrCode && (
                          <div className="flex flex-col sm:flex-row gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center"
                              onClick={downloadQRCode}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download QR Code
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center"
                              onClick={printQRCode}
                            >
                              <QrCode className="h-4 w-4 mr-2" />
                              Print QR Code
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recipient & Dates Section */}
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900 border-b pb-3">
                  Recipient & Dates
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Issued To */}
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500">
                      Issued To
                    </h3>
                    <div>
                      <div className="font-medium text-gray-900">
                        {selectedGiftCard.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {selectedGiftCard.email}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {selectedGiftCard.mobileNo || "N/A"}
                      </div>
                    </div>
                  </div>

                  {/* Created By */}
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500">
                      Created By
                    </h3>
                    <div>
                      <div className="font-medium text-gray-900">
                        {selectedGiftCard.createdByName || "N/A"}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {format(
                          new Date(selectedGiftCard.createdDateTime),
                          "MMM dd, yyyy"
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {format(
                          new Date(selectedGiftCard.createdDateTime),
                          "HH:mm"
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Personal Message & Occasion Section */}
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900 border-b pb-3">
                  Personal Message & Occasion
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Personal Message */}
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500">
                      Personal Message
                    </h3>
                    <div className="text-sm text-gray-900">
                      {selectedGiftCard.personalMessage &&
                      selectedGiftCard.personalMessage.trim() !== ""
                        ? selectedGiftCard.personalMessage
                        : "N/A"}
                    </div>
                  </div>

                  {/* Occasion */}
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500">
                      Occasion
                    </h3>
                    <div className="text-sm text-gray-900">
                      {selectedGiftCard.occasion &&
                      selectedGiftCard.occasion.trim() !== ""
                        ? selectedGiftCard.occasion
                        : "N/A"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Status & Usage Section */}
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900 border-b pb-3">
                  Status & Usage
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Status */}
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500">
                      Status
                    </h3>
                    {getStatusBadge(selectedGiftCard.issuedCardStatus)}
                  </div>

                  {/* Usage Section */}
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500">Usage</h3>
                    <div className="space-y-3">
                      {(() => {
                        const usedAmount =
                          selectedGiftCard.amount - selectedGiftCard.balance;
                        const usagePercentage =
                          selectedGiftCard.amount > 0
                            ? (usedAmount / selectedGiftCard.amount) * 100
                            : 0;

                        const currency =
                          localStorage.getItem("giftCardCurrency") || "USD";
                        return (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">
                                Used: {usedAmount.toFixed(2)} {currency}
                              </span>
                              <span
                                className={
                                  selectedGiftCard.balance === 0
                                    ? "text-red-600"
                                    : "text-gray-600"
                                }
                              >
                                Remaining: {selectedGiftCard.balance.toFixed(2)}{" "}
                                {currency}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div
                                className="bg-green-500 h-3 rounded-full transition-all duration-300"
                                style={{
                                  width: `${usagePercentage.toFixed(1)}%`,
                                }}
                              ></div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Redemption History Section */}
              <div className="space-y-4">
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-amber-50 text-amber-700 hover:bg-amber-100"
                    disabled={
                      isHistoryLoading ||
                      !selectedGiftCard?.issuedCardNumber ||
                      selectedGiftCard.issuedCardNumber.trim() === ""
                    }
                    onClick={() => {
                      if (historyVisible) {
                        setHistoryVisible(false);
                        return;
                      }
                      if (!selectedGiftCard?.issuedCardNumber) {
                        toast.error("Card number not available for history.");
                        return;
                      }
                      fetchRedemptionHistory(selectedGiftCard.issuedCardNumber);
                    }}
                  >
                    {historyVisible
                      ? "Hide Redemption History"
                      : isHistoryLoading
                      ? "Loading..."
                      : "View Redemption History"}
                  </Button>
                </div>

                {historyVisible && (
                  <div className="border rounded-lg bg-gray-50 p-4 space-y-4">
                    {historyError ? (
                      <p className="text-sm text-red-500">{historyError}</p>
                    ) : giftCardHistory.length === 0 && !isHistoryLoading ? (
                      <p className="text-sm text-gray-500">
                        No redemption history available.
                      </p>
                    ) : isHistoryLoading ? (
                      <p className="text-sm text-gray-500">Loading...</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-white">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Brand Name
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Amount Deducted
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Redeemed Date
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {giftCardHistory.map((entry, index) => (
                              <tr key={index}>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {entry.merchantName || "N/A"}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {entry.amountDeducted.toFixed(2)}{" "}
                                  {localStorage.getItem("giftCardCurrency") ||
                                    "USD"}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {entry.redeemedDate
                                    ? format(
                                        new Date(entry.redeemedDate),
                                        "MMM dd, yyyy HH:mm"
                                      )
                                    : "N/A"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllGiftCards;
